/**
 * @file HowlerAudioBackend.ts
 * @role The Howler-backed AudioBackend. This is the SINGLE place that imports Howler, so the
 *       rest of the app (and the AudioService's unit tests) never touch WebAudio. Wraps a Howl
 *       per sound behind the tiny SoundHandle interface.
 * @owns Howl creation + the unlock (audio-context resume) call.
 * @reads Sound source URLs passed in.
 * @neverDoes Own bus/volume policy (AudioService does). Decide what to play.
 * @invariants Audio rule 1 (only this boundary uses Howler).
 * @see src/core/audio/AudioService.ts
 */
import { Howl, Howler } from 'howler';
import type { AudioBackend, SoundCreateOptions, SoundHandle } from './AudioService';

export class HowlerAudioBackend implements AudioBackend {
  create(options: SoundCreateOptions): SoundHandle {
    const selectedFormat = options.src
      .map((source) => source.split(/[?#]/, 1)[0]?.split('.').pop()?.toLowerCase())
      .find((format) => Boolean(format && Howler.codecs(format)));
    const howl = new Howl({
      src: [...options.src],
      loop: options.loop,
      html5: options.html5,
      pool: options.pool,
      preload: true,
      onload: options.onLoad,
      onloaderror: options.onLoadError,
      onend: options.onEnd,
    });
    return {
      selectedFormat,
      play: () => howl.play(),
      stop: (playbackId) =>
        playbackId === undefined ? void howl.stop() : void howl.stop(playbackId),
      setVolume: (volume, playbackId) =>
        playbackId === undefined ? void howl.volume(volume) : void howl.volume(volume, playbackId),
      setRate: (rate, playbackId) =>
        playbackId === undefined ? void howl.rate(rate) : void howl.rate(rate, playbackId),
      fadeTo: (volume, durationMs, playbackId) => {
        const from = (playbackId === undefined ? howl.volume() : howl.volume(playbackId)) as number;
        if (playbackId === undefined) howl.fade(from, volume, durationMs);
        else howl.fade(from, volume, durationMs, playbackId);
      },
      fadeOutAndStop: (durationMs, playbackId) => {
        const from = (playbackId === undefined ? howl.volume() : howl.volume(playbackId)) as number;
        if (playbackId === undefined) {
          howl.once('fade', () => howl.stop());
          howl.fade(from, 0, durationMs);
        } else {
          howl.once('fade', () => howl.stop(playbackId), playbackId);
          howl.fade(from, 0, durationMs, playbackId);
        }
      },
      unload: () => void howl.unload(),
    };
  }

  unlock(): void {
    // Resume the WebAudio context if the browser suspended it pending a user gesture.
    const ctx = Howler.ctx;
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  }
}
